import { useEffect } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { useFoldersMutating, useFoldersMutations } from '../../hooks'
import type { Folder } from '../../stores/types'

const dialogId = 'rename-folder'

interface FolderFormData {
  name?: string
}

export const RenameFolderDialog = () => {
  const { successToast, errorToast } = useToast()
  const open = useDialogIsOpen(dialogId)
  const folder = useDialogData() as Folder
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldError, setFieldValue } = useForm()
  const { updateFolder } = useFoldersMutations()
  const isUpdating = useFoldersMutating()

  useEffect(() => {
    if (!folder || !open) return
    Object.keys(folder).forEach((field) =>
      setFieldValue(field, folder[field as keyof Folder] as string),
    )
  }, [open, folder, setFieldValue])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { name } = data as FolderFormData

    if (!name || name.trim().length === 0) {
      setFieldError('name', 'Name is required')
      return
    }

    updateFolder(folder.slug, { name })
      .then(() => {
        closeDialog(true)
        successToast('Updated folder successfully')
      })
      .catch((error) => errorToast('Failed to update folder', error))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Folder</DialogTitle>
          <DialogDescription>
            Enter a new name for the folder "{folder?.name}".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <TextField
              label="Folder Name"
              id="folder-name"
              placeholder="Enter folder name"
              value={data?.name as string}
              error={errors?.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('name', e.target.value)
              }
              required
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isUpdating}>
              Rename Folder
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

RenameFolderDialog.id = dialogId
